import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import { HttpStatusCode } from "axios";
import { getApiErrorStatus } from "_shared/_errors/api-error-message";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import {
  CreatePolicyReplacementCommandJSON,
  PolicyReplacementCreatedJSON,
  PolicyReplacementCreatedJSONZ,
  PolicyReplacementJSON,
  PolicyReplacementJSONZ,
  PolicyReplacementPreviewJSON,
  PolicyReplacementPreviewJSONZ,
  PolicyReplacementPreviewRequestJSON,
} from "work/policy-replacements/_schemas/policy-replacement-schema";

type PolicyReplacementIdArgs = { policyReplacementId: string };
type PolicyReplacementTargetArgs = PolicyReplacementIdArgs & { workAgreementId: string };

type ChangeLifecycle = {
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>;
  queryFulfilled: Promise<{ data: PolicyReplacementJSON }>;
};

/** Whether a failed mutation was refused because the replacement's state moved on (409). */
function wasRefusedForItsState(rejection: unknown): boolean {
  return (
    typeof rejection === "object" &&
    rejection !== null &&
    "error" in rejection &&
    getApiErrorStatus(rejection.error) === HttpStatusCode.Conflict
  );
}

/**
 * What the page holds after a withdraw or a retry.
 *
 * A 200 answers with the whole replacement as it now stands, so the page takes that answer instead
 * of asking again; a withdrawal cancels a revision of a work agreement, so the agreements are asked
 * again too. A 409 carries no replacement. The bulk withdrawal answers 409 when it withdrew some
 * targets and not others, and a single target answers it when its state moved on, so the page asks
 * for the replacement again to show what did change. The page reports the failure itself.
 */
async function takeTheAnswer(policyReplacementId: string, { dispatch, queryFulfilled }: ChangeLifecycle): Promise<void> {
  try {
    const { data } = await queryFulfilled;
    dispatch(
      policyReplacementsApi.util.upsertQueryEntries([
        { endpointName: "getPolicyReplacement", arg: { policyReplacementId }, value: data },
      ]),
    );
    dispatch(policyReplacementsApi.util.invalidateTags([{ type: "WorkAgreement", id: "LIST" }]));
  } catch (rejection) {
    if (!wasRefusedForItsState(rejection)) return;
    void dispatch(
      policyReplacementsApi.endpoints.getPolicyReplacement.initiate(
        { policyReplacementId },
        { forceRefetch: true, subscribe: false },
      ),
    );
  }
}

/**
 * `work/policy-replacements`: replacing one client policy document across many work agreements, one
 * revision each. See `docs/design/proposals-negotiation/POLICY_REPLACEMENT_NEGOTIATION_DESIGN.md`.
 *
 * No cache tags: the preview is keyed by what it was asked, and the page's replacement changes here
 * only through withdraw and retry, whose answers `takeTheAnswer` puts in its place. A change made
 * anywhere else reaches the progress page through its own fresh reads.
 */
export const policyReplacementsApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Which agreements a replacement would reach, and what it would do to each. A POST because the
     * agreements added by hand do not fit a query string; it writes nothing, so it is a query.
     */
    previewPolicyReplacement: build.query<PolicyReplacementPreviewJSON, PolicyReplacementPreviewRequestJSON>({
      query: (request) => ({
        url: "work/policy-replacements/preview",
        method: "POST",
        body: request,
      }),
      rawResponseSchema: PolicyReplacementPreviewJSONZ,
      onSchemaFailure: logSchemaFailure,
    }),

    /**
     * Records the replacement. 201 answers every selected agreement; 422 carries the same body when
     * no agreement could take it and nothing was created, so the page reads the body from the error.
     */
    createPolicyReplacement: build.mutation<PolicyReplacementCreatedJSON, CreatePolicyReplacementCommandJSON>({
      query: (command) => ({
        url: "work/policy-replacements",
        method: "POST",
        body: command,
      }),
      rawResponseSchema: PolicyReplacementCreatedJSONZ,
      onSchemaFailure: logSchemaFailure,
    }),

    getPolicyReplacement: build.query<PolicyReplacementJSON, PolicyReplacementIdArgs>({
      query: ({ policyReplacementId }) => `work/policy-replacements/${policyReplacementId}`,
      rawResponseSchema: PolicyReplacementJSONZ,
      onSchemaFailure: logSchemaFailure,
    }),

    /**
     * Withdraws every target whose revision is not yet hired (#6255). Answers 409 when it could
     * withdraw only some of them; withdrawing again finishes the rest.
     */
    withdrawPolicyReplacement: build.mutation<PolicyReplacementJSON, PolicyReplacementIdArgs>({
      query: ({ policyReplacementId }) => ({
        url: `work/policy-replacements/${policyReplacementId}/withdraw`,
        method: "POST",
      }),
      rawResponseSchema: PolicyReplacementJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: ({ policyReplacementId }, lifecycle) => takeTheAnswer(policyReplacementId, lifecycle),
    }),

    /** Withdraws one agreement's part, cancelling its revision when one is open (#6255). */
    withdrawPolicyReplacementTarget: build.mutation<PolicyReplacementJSON, PolicyReplacementTargetArgs>({
      query: ({ policyReplacementId, workAgreementId }) => ({
        url: `work/policy-replacements/${policyReplacementId}/targets/${workAgreementId}/withdraw`,
        method: "POST",
      }),
      rawResponseSchema: PolicyReplacementJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: ({ policyReplacementId }, lifecycle) => takeTheAnswer(policyReplacementId, lifecycle),
    }),

    /** Hands a stopped or needs-a-person target back to the worker (#6255). */
    retryPolicyReplacementTarget: build.mutation<PolicyReplacementJSON, PolicyReplacementTargetArgs>({
      query: ({ policyReplacementId, workAgreementId }) => ({
        url: `work/policy-replacements/${policyReplacementId}/targets/${workAgreementId}/retry`,
        method: "POST",
      }),
      rawResponseSchema: PolicyReplacementJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: ({ policyReplacementId }, lifecycle) => takeTheAnswer(policyReplacementId, lifecycle),
    }),
  }),
});

export const {
  usePreviewPolicyReplacementQuery,
  useCreatePolicyReplacementMutation,
  useGetPolicyReplacementQuery,
  useWithdrawPolicyReplacementMutation,
  useWithdrawPolicyReplacementTargetMutation,
  useRetryPolicyReplacementTargetMutation,
} = policyReplacementsApi;
